import * as React from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Button, makeStyles, MessageBar, MessageBarBody, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { useFormikContext } from 'formik'
import useAxios from '../../../../hooks/useAxios'
import Loading from '../../../../components/Loading'
import { SortDirection } from '../../../../config/settingConfig'
import { AxiosError, AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    useOverviewStyles,
} from '../../../../pages/BaseOverview/BaseOverview'
import { BaseOverviewFilterType, generateFromDate, SearchHintIcon } from '../../../../pages/BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../../../../pages/BaseOverview/BaseSortMenu'
import { Message } from '@microsoft/microsoft-graph-types'
import { EntityDto } from '../../../../config/appConfig'
import FileEmailListItem from './FileEmailListItem'
import { ViewFileProps } from '../ViewFile'
import { Destination, IGraphArrayPayload } from '../../../../context/AxiosContext'
import { extractReferenceNumbersFromMessage, FILE_REFERENCE_NUMBER_PROP } from '../../../../helpers/referenceNumberHelper'
import BaseHeaderItem from '../../../../pages/BaseOverview/BaseHeaderItem'
import useUser from '../../../../hooks/useUser'
import HammockDialog, { useDialogStyles } from '../../../components/Dialog/HammockDialog'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import PreviewMessageDialogBody, { PreviewMessageDialogBodyProps } from '../../../components/Dialog/Message/PreviewMessageDialogBody'
import FileEmailHeaderActionsButtons, { EmailHeaderActionsOverviewProps } from './FileEmailHeaderActionsButtons'
import FileEmailHeaderFilterTags, { EmailHeaderFilterTagsOverviewProps } from './FileEmailHeaderFilterTags'
import { useTranslation } from 'react-i18next'
import DateRange from '../../../../components/DatePicker/DateRange'
import { selectRangeByOrder, toggleSelect } from '../../../../helpers/selectionHelper'

export const useItemStyles = makeStyles({
    listEmailItem: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewIconWidth) auto var(--baseOverviewDateWidth)',
    },
    listEmailDetails: {
        display: 'grid',
        gridTemplateColumns: '1fr`',
        gridTemplateRows: '1fr 1fr 1fr`',
    },
})

export type ExtendedEmailOverviewDto = Omit<Message, 'id'> &
    EntityDto & {
        inbound?: boolean
        references?: string[]
        url?: string //Custom URL with which the item can be downloaded.
    }

export type EmailOverviewProps = BaseOverviewProps<
    ExtendedEmailOverviewDto,
    EmailFilterType,
    EmailSortingType,
    BaseOverviewCount,
    EmailHeaderActionsOverviewProps,
    EmailHeaderFilterTagsOverviewProps
> &
    ViewFileProps

export type EmailLineOverviewProps = EmailOverviewProps &
    BaseOverviewLineItemProps<ExtendedEmailOverviewDto, EmailFilterType, EmailSortingType> & {
        onPreviewClick: (message: ExtendedEmailOverviewDto) => void
    }

export type EmailFilterType = BaseOverviewFilterType & {
    from?: string
    to?: string
}

export type EmailSorting = {
    sentDateTime: string
}

export type EmailSortingType = BaseOverviewSortingType<EmailSorting>

function EmailFilter() {
    const { values, setFieldValue } = useFormikContext<EmailFilterType>()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.OutlookNewWide) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
        </>
    )
}

function FilterInputHint() {
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    return (
        <Popover open={open} onOpenChange={(_e, data) => setOpen(data.open)} withArrow>
            <PopoverTrigger disableButtonEnhancement>
                <Button size={'small'} appearance={'transparent'} icon={<SearchHintIcon />} />
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
                <h3>{translate('file.pivot.email.search.hint.title')}</h3>
                <br />
                <div dangerouslySetInnerHTML={{ __html: translate('file.pivot.email.search.hint.body') }}></div>
            </PopoverSurface>
        </Popover>
    )
}

const headerSortingItems: EmailSortingType[] = [
    { property: ['sentDateTime'], direction: SortDirection.Ascending, descriptionKey: 'sorting.dateasc' },
    { property: ['sentDateTime'], direction: SortDirection.Descending, default: true, descriptionKey: 'sorting.datedesc' },
]

//Graph does not allow $skip/$filter/$orderby in combination with $search, so search results are filtered, sorted and paged client-side.
const SEARCH_MAX_RESULTS = 500

function applySearchFilters(
    items: ExtendedEmailOverviewDto[],
    filterValues: EmailFilterType,
    sortingValues?: EmailSortingType,
): ExtendedEmailOverviewDto[] {
    //Plain string comparison mirrors the midnight-UTC semantics of the browse-mode Graph $filter on sentDateTime.
    const filtered = items.filter(
        item => (!filterValues.from || item.sentDateTime >= filterValues.from) && (!filterValues.to || item.sentDateTime <= filterValues.to),
    )
    if (!sortingValues) {
        return filtered
    }
    return filtered.sort(
        (a, b) => (sortingValues.direction === SortDirection.Ascending ? 1 : -1) * (a.sentDateTime ?? '').localeCompare(b.sentDateTime ?? ''),
    )
}

export default function FileEmailOverview(props: Partial<EmailOverviewProps> & ViewFileProps) {
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const dialogStyles = useDialogStyles()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { currentMailbox } = useUser()
    const { t: translate } = useTranslation()

    const overviewApiRef = useRef<{
        onUpdatePageItems: (column: number, page: number, start: number, size: number) => Promise<ExtendedEmailOverviewDto[]>
    }>()
    const [reloadTrigger, setReloadTrigger] = useState<number>(0)
    const searchCacheRef = useRef<{ key: string; promise: Promise<ExtendedEmailOverviewDto[]> }>()
    const [searchTruncated, setSearchTruncated] = useState<boolean>(false)
    //HTTP status of the last failed search resp. failed browse, or 0 when the request never got a response.
    const [searchErrorStatus, setSearchErrorStatus] = useState<number>()
    const [listErrorStatus, setListErrorStatus] = useState<number>()
    const reload = () => {
        searchCacheRef.current = undefined
        setSearchErrorStatus(undefined)
        setListErrorStatus(undefined)
        setReloadTrigger(reloadTrigger + 1)
    }

    const [emailReplicationFolderId, setEmailReplicationFolderId] = useState<string | null>()
    const [emailUserFolderId, setEmailUserFolderId] = useState<string>()

    const [count, setCount] = useState<BaseOverviewCount>({ total: 0 })
    const [items, setItems] = useState<ExtendedEmailOverviewDto[]>([])
    const [selectedItems, setSelectedItems] = useState<ExtendedEmailOverviewDto[]>([])
    const [openPreviewMessage, setOpenPreviewMessage] = useState<ExtendedEmailOverviewDto>()

    //The pages as BaseOverview holds them, gaps included: the flattened item list drops the
    //not-yet-loaded pages, so only this keeps index === position in the folder.
    const pagesRef = useRef<ExtendedEmailOverviewDto[][]>([])
    //Start of a Shift+click range; it survives a range so a follow-up Shift+click extends from the same origin.
    const anchorRef = useRef<{ id: string; index: number }>()
    //A range can span pages that are not loaded yet; the actions wait until they are in.
    const [rangeLoading, setRangeLoading] = useState<boolean>(false)

    const clearSelection = useCallback(() => {
        anchorRef.current = undefined
        setSelectedItems([])
    }, [])

    const applySelection = useCallback((selectedIds: Set<string>, known: ExtendedEmailOverviewDto[]) => {
        setSelectedItems(previous => {
            const byId = new Map(previous.map(item => [item.id, item]))
            known.forEach(item => byId.set(item.id, item))
            return [...selectedIds].map(id => byId.get(id)).filter(Boolean)
        })
    }, [])

    //Shift+click: select every message between the anchor and the clicked row. Rows on pages that
    //were never scrolled into view are fetched first, so the range is complete instead of partial.
    const selectRangeTo = useCallback(
        (target: { id: string; index: number }, batchSize: number) => {
            const anchor = anchorRef.current
            const from = Math.min(anchor.index, target.index)
            const to = Math.max(anchor.index, target.index)
            const missingPages: number[] = []
            for (let page = Math.floor(from / batchSize); page <= Math.floor(to / batchSize); page++) {
                if (pagesRef.current[page] === undefined) {
                    missingPages.push(page)
                }
            }

            setRangeLoading(missingPages.length > 0)
            //Sequential: onUpdatePageItems mutates one shared page array, and Graph throttles bursts.
            missingPages
                .reduce(
                    (previous, page) => previous.then(() => overviewApiRef.current?.onUpdatePageItems(0, page, page * batchSize, batchSize)),
                    Promise.resolve(undefined),
                )
                .then(() => {
                    const rangeItems: ExtendedEmailOverviewDto[] = []
                    for (let index = from; index <= to; index++) {
                        const item = pagesRef.current[Math.floor(index / batchSize)]?.[index % batchSize]
                        if (item) {
                            rangeItems.push(item)
                        }
                    }
                    const result = selectRangeByOrder(
                        rangeItems.map(item => item.id),
                        anchor.id,
                        target.id,
                        new Set(selectedItems.map(item => item.id)),
                    )
                    anchorRef.current = { id: result.anchor ?? target.id, index: result.anchor === anchor.id ? anchor.index : target.index }
                    applySelection(result.next, rangeItems)
                })
                .finally(() => setRangeLoading(false))
        },
        [selectedItems, applySelection],
    )

    //Escape clears the selection, mirroring the document overview. Document-wide, but only while
    //this tab is mounted; typing in a field, dialog or menu is left alone.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            if (target?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"], [role="listbox"]')) {
                return
            }
            if (event.key === 'Escape') {
                clearSelection()
            }
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [clearSelection])

    useEffect(() => {
        axios
            .get('/email/replication/parent-folder/id', { params: { references: [props.file.reference] } })
            .then((response: AxiosResponse<{ [key: string]: string }>) => setEmailReplicationFolderId(response.data[props.file.reference] ?? null))
    }, [props.file])

    useEffect(() => {
        axios
            .get(`/email/${currentMailbox}/folder/${props.file.reference}`, {
                baseURL: Destination.Hammock,
                headers: { SuppressErrors: 'true' },
            })
            // 204 (no folder linked) resolves with an empty body; normalize to undefined.
            .then((response: AxiosResponse<string>) => setEmailUserFolderId(response.data || undefined))
    }, [props.file])

    const defaultFilterValues = useMemo<EmailFilterType>(
        () => ({
            from: '',
            to: '',
            query: '',
            tag: 'all',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const mapMessage = useCallback(
        (item: Message): ExtendedEmailOverviewDto => ({
            id: item.id,
            ...item,
            references: extractReferenceNumbersFromMessage(item),
            inbound:
                item.sender?.emailAddress.address
                    .substring(item.sender?.emailAddress.address.indexOf('@'))
                    .localeCompare(currentMailbox?.substring(currentMailbox?.indexOf('@'))) !== 0,
            url: `${process.env.INBOXER_HOST}/api/download-message?hostKey=${currentHost?.RowKey}&email=${encodeURIComponent(currentHost.ReplicationMail)}&messageId=${item.id}`,
        }),
        [currentMailbox, currentHost],
    )

    //Both browse calls suppress their errors, so record the failure for the message bar below; BaseOverview drops the rejection.
    const recordListError = useCallback((error: AxiosError) => {
        if (error.message !== 'canceled') {
            setListErrorStatus(error.response?.status ?? 0)
        }
    }, [])

    const fetchSearchResults = useCallback(
        (query: string): Promise<ExtendedEmailOverviewDto[]> => {
            const key = `${emailReplicationFolderId}|${props.file.reference}|${query}`
            if (searchCacheRef.current?.key === key) {
                return searchCacheRef.current.promise
            }
            setSearchErrorStatus(undefined)
            const promise = axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select:
                            'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                        $top: SEARCH_MAX_RESULTS,
                        //The folder already scopes to the file; the reference guards against Graph ignoring folder scope with $search.
                        $search: `"${query} ${props.file.reference}"`,
                    },
                })
                .then((response: AxiosResponse<IGraphArrayPayload<Message>>): ExtendedEmailOverviewDto[] => {
                    const value = response.data.value || []
                    setSearchTruncated(value.length >= SEARCH_MAX_RESULTS)
                    return value.map(mapMessage)
                })
                //The errors of this call are suppressed, so record the failure for the message bar below; BaseOverview drops the rejection.
                .catch((error: AxiosError) => {
                    searchCacheRef.current = undefined
                    if (error.message !== 'canceled') {
                        setSearchTruncated(false)
                        setSearchErrorStatus(error.response?.status ?? 0)
                    }
                    throw error
                })
            searchCacheRef.current = { key, promise }
            return promise
        },
        [props.file, emailReplicationFolderId, mapMessage],
    )

    const countItems = useCallback(
        (_column?: number, filterValues?: EmailFilterType): Promise<BaseOverviewCount> => {
            if (filterValues.query) {
                return fetchSearchResults(filterValues.query).then(results => ({
                    total: applySearchFilters(results, filterValues).length,
                }))
            }
            setSearchTruncated(false)
            setSearchErrorStatus(undefined)
            setListErrorStatus(undefined)
            return axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select: 'id',
                        $filter: [
                            `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and contains(ep/value, '[${props.file.reference}]'))`,
                            filterValues.from && `sentDateTime ge ${filterValues.from}`,
                            filterValues.to && `sentDateTime le ${filterValues.to}`,
                        ]
                            .filter(Boolean)
                            .join(' and '),
                        $count: true,
                    },
                })
                .then((response: AxiosResponse<IGraphArrayPayload<Message>>): BaseOverviewCount => ({
                    total: response.data['@odata.count'] ?? response.data.value.length ?? 0,
                }))
                .catch((error: AxiosError) => {
                    recordListError(error)
                    throw error
                })
        },
        [props.file, emailReplicationFolderId, fetchSearchResults, recordListError],
    )

    const loadItems = useCallback(
        (
            _column?: number,
            filterValues?: EmailFilterType,
            sortingValues?: EmailSortingType,
            start?: number,
            size?: number,
        ): Promise<ExtendedEmailOverviewDto[]> => {
            if (filterValues.query) {
                return fetchSearchResults(filterValues.query).then(results =>
                    applySearchFilters(results, filterValues, sortingValues).slice(start, start + size),
                )
            }
            return axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select:
                            'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                        $filter: [
                            `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and contains(ep/value, '[${props.file.reference}]'))`,
                            filterValues.from && `sentDateTime ge ${filterValues.from}`,
                            filterValues.to && `sentDateTime le ${filterValues.to}`,
                        ]
                            .filter(Boolean)
                            .join(' and '),
                        $expand: `singleValueExtendedProperties($filter=id eq '${FILE_REFERENCE_NUMBER_PROP}')`,
                        $top: `${size}`,
                        $skip: `${start}`,
                        $orderby: `${sortingValues.property[0]} ${sortingValues.direction.toLowerCase()}`,
                    },
                })
                .then((response: AxiosResponse<IGraphArrayPayload<Message>>): ExtendedEmailOverviewDto[] =>
                    (response.data.value || []).map(mapMessage),
                )
                .catch((error: AxiosError) => {
                    recordListError(error)
                    throw error
                })
        },
        [props.file, emailReplicationFolderId, fetchSearchResults, mapMessage, recordListError],
    )

    if (emailReplicationFolderId === undefined) {
        return <Loading />
    }
    if (emailReplicationFolderId === null) {
        return (
            <MessageBar intent={'error'}>
                <MessageBarBody>{translate('file.pivot.email.error.no_folder')}</MessageBarBody>
            </MessageBar>
        )
    }

    return (
        <>
            <HammockDialog<PreviewMessageDialogBodyProps>
                open={openPreviewMessage !== undefined}
                setOpen={(open: boolean) => setOpenPreviewMessage(open ? openPreviewMessage : undefined)}
                dialogBody={PreviewMessageDialogBody}
                surfaceProps={{
                    className: dialogStyles.dialogHeight,
                }}
                dialogBodyProps={() => ({
                    index: items.indexOf(openPreviewMessage),
                    message: openPreviewMessage,
                    destination: Destination.ReplicationEmail,
                    hasPrevious: items.indexOf(openPreviewMessage) > 0,
                    hasNext: count.total > items.indexOf(openPreviewMessage) + 1,
                    previous: () => setOpenPreviewMessage(items[items.indexOf(openPreviewMessage) - 1]),
                    next: () => {
                        ;(items.length > items.indexOf(openPreviewMessage) + 1
                            ? Promise.resolve(items)
                            : overviewApiRef.current?.onUpdatePageItems?.(0, undefined, items.length, undefined)
                        ).then(_items => {
                            setOpenPreviewMessage(_items[_items.indexOf(openPreviewMessage) + 1])
                        })
                    },
                })}
            />

            {listErrorStatus !== undefined && (
                <MessageBar intent={'error'}>
                    <MessageBarBody>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: translate(
                                    //Graph answers 404 instead of 403 when the mailbox itself cannot be opened, so the folder looks absent.
                                    listErrorStatus === 403
                                        ? 'file.pivot.email.error.no_access'
                                        : listErrorStatus === 404
                                          ? 'file.pivot.email.error.no_folder_access'
                                          : 'file.pivot.email.error.failed',
                                ),
                            }}
                        ></span>
                    </MessageBarBody>
                </MessageBar>
            )}

            {listErrorStatus === undefined && searchErrorStatus !== undefined && (
                <MessageBar intent={'error'}>
                    <MessageBarBody>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: translate(
                                    searchErrorStatus === 403 ? 'file.pivot.email.search.error.forbidden' : 'file.pivot.email.search.error.failed',
                                ),
                            }}
                        ></span>
                    </MessageBarBody>
                </MessageBar>
            )}

            {searchTruncated && (
                <MessageBar intent={'warning'}>
                    <MessageBarBody>{translate('file.pivot.email.search.truncated', { count: SEARCH_MAX_RESULTS })}</MessageBarBody>
                </MessageBar>
            )}

            <BaseOverview<
                ExtendedEmailOverviewDto,
                EmailFilterType,
                EmailSortingType,
                BaseOverviewCount,
                EmailHeaderActionsOverviewProps,
                EmailHeaderFilterTagsOverviewProps
            >
                defaultFilterValues={defaultFilterValues}
                countItems={countItems}
                loadItems={loadItems}
                onCountItems={(_column, result) => {
                    setCount(result)
                    //BaseOverview drops its pages on a count refresh; keep the mirror in step.
                    pagesRef.current = []
                    //Fires on a filter, sort, search or reload change — never on paging in more rows,
                    //so a selection survives scrolling but not a list that changed underneath it.
                    clearSelection()
                }}
                onLoadItems={(_column, columnPages, items) => {
                    pagesRef.current = columnPages[0] ?? []
                    setItems([...items])
                }}
                listEmptyPlaceholderKey={'file.pivot.email.error.no_content'}
                listItemHeaderContent={BaseHeaderItem}
                listItemHeaderClassnames={() => [overviewStyles.listBaseHeaderItem]}
                listItemContent={(props: EmailLineOverviewProps) => (
                    <FileEmailListItem {...props} onPreviewClick={item => setOpenPreviewMessage(item)} />
                )}
                listItemClassnames={() => [itemStyles.listEmailItem]}
                listColumnFullWidth
                headerActionsButtons={FileEmailHeaderActionsButtons}
                headerActionsButtonsProps={{
                    emailUserFolderId: emailUserFolderId,
                    setEmailUserFolderId: setEmailUserFolderId,
                    reload: () => {
                        clearSelection()
                        reload()
                    },
                }}
                headerDefaultFilterTags={FileEmailHeaderFilterTags}
                headerDefaultFilterTagsProps={{
                    selectedItems: selectedItems,
                    selectionLoading: rangeLoading,
                    clearSelection: clearSelection,
                    reload: () => {
                        clearSelection()
                        reload()
                    },
                }}
                headerSortingItems={headerSortingItems}
                filterContent={EmailFilter}
                filterInputHint={FilterInputHint}
                filterFromJson={values => ({
                    ...values,
                    from: values.tag ? generateFromDate(values.tag) : values.from,
                    // to: values.tag ? generateToDate(values.tag) : values.to,
                })}
                filterToJson={values => ({
                    ...values,
                    from: values.tag ? '' : values.from,
                    // to: values.tag ? '' : values.to,
                    icon: undefined,
                    title: undefined,
                })}
                inputPlaceholderKey={'email.search.placeholder'}
                localStorageKeyPrefix={'HammockFileEmailOverview'}
                apiRef={overviewApiRef}
                reloadTrigger={reloadTrigger}
                isItemSelectable={() => true}
                //Match on id: paging in more rows hands out new objects for the same message.
                isItemSelected={item => selectedItems.some(selected => selected.id === item.id)}
                onItemSelected={(data, item) => {
                    if (data.checked === true) {
                        setSelectedItems(prevItems => [...new Set([...prevItems, item])])
                    } else {
                        setSelectedItems(prevItems => [...new Set([...prevItems.filter(prevItem => prevItem.id !== item.id)])])
                    }
                }}
                onAllItemsSelected={(data, items) => {
                    if (data.checked === true) {
                        setSelectedItems(items)
                    } else {
                        setSelectedItems([])
                    }
                }}
                onItemClicked={(lineProps, event) => {
                    const batchSize = lineProps.batchSizeItems ?? 20
                    //`index` counts within the page; the folder position is what a range needs.
                    const clicked = { id: lineProps.item.id, index: lineProps.pageIndex * batchSize + lineProps.index }
                    if (event?.shiftKey && anchorRef.current) {
                        //Shift+click would otherwise leave the browser's text selection behind.
                        event.preventDefault()
                        window.getSelection()?.removeAllRanges()
                        selectRangeTo(clicked, batchSize)
                        return
                    }
                    const result = toggleSelect(new Set(selectedItems.map(item => item.id)), clicked.id)
                    anchorRef.current = clicked
                    applySelection(result.next, [lineProps.item])
                }}
                {...props}
            />
        </>
    )
}
